import * as echarts from "echarts";
import darkTheme from "hb/insights/chart_dark_theme";

import ApplicationController from "../../application_component_controller";

import URL from "hb/lib/url";
import { useOutlets } from "hb/insights/use_outlets";
import { useResize } from "hb/insights/use_resize";
import { useListen } from "hb/insights/use_listen";
import { DEFAULT_QUERY } from "hb/insights/query";

import hljs from "highlight.js/lib/core";

export default class extends ApplicationController {
  static values = {
    loadEcharts: Boolean,
  };

  static targets = [
    "chart",
    "chartWrap",
    "error",
    // Templates
    "emptyQueryTemplate",
    "errorMessageTemplate",
  ];

  connect() {
    if (this.loadEchartsValue) {
      this.loadChart();
      useResize(this, { element: this.chartTarget });
    }

    useOutlets(this);
    useListen(this);

    this.listenWindow("chart:clearCrosshair", this.clearCrosshair);
    this.listenWindow("chart:crosshairHighlight", this.crosshairHighlight);

    this.highlighting = false;
  }

  loadChart() {
    var theme = null;
    if (
      document.documentElement.getAttribute("data-bs-theme") === "dark" ||
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      theme = darkTheme;
    }

    this.chart = echarts.init(this.chartTarget, theme, {
      useCoarsePointer: true,
    });
    this.chartTarget.children[0].style.position = "absolute";
    this.loadEvents();
  }

  setChartHtml(html) {
    this.chartTarget.innerHTML = html;
  }

  disconnect() {
    if (this.chart) {
      echarts.dispose(this.chart);
    }
  }

  loadEvents() {
    this.chart?.on("globalout", () => {
      this.highlighting = false;
      window.dispatchEvent(new Event("chart:clearCrosshair"));
    });

    this.chart?.on("highlight", (e) => {
      if (!e.batch) return;
      this.highlighting = true;
      const { dataIndex, seriesIndex } = e.batch[0];
      const options = this.chart.getOption();
      const xAxis = options.xAxis[0];
      if (xAxis.type === "time") {
        const dataset = options.dataset[0];

        // Holy cow, the way echarts handles x dimension fields is so weird
        let xDimension;
        if (dataset.dimensions[0]?.name && dataset.dimensions[0]?.name !== "") {
          xDimension = dataset.dimensions[0].name;
        } else if (
          dataset.dimensions[0] !== undefined &&
          typeof dataset.dimensions[0] === "string"
        ) {
          xDimension = dataset.dimensions[0];
        } else if (options.series[seriesIndex].dimensions[0] !== undefined) {
          xDimension = options.series[seriesIndex].dimensions[0];
        } else if (xAxis.name !== "") {
          xDimension = xAxis.name;
        } else {
          // Forget it
          return;
        }

        const timestamp = options.dataset[0].source[dataIndex][xDimension];
        window.dispatchEvent(
          new CustomEvent("chart:crosshairHighlight", {
            detail: { timestamp },
          }),
        );
      }
    });
  }

  clearCrosshair() {
    if (this.highlighting || this.chart === undefined) return;

    this.chart.setOption({
      graphic: [
        {
          id: "crosshair",
          z: 1000,
          type: "line",
          invisible: true,
        },
      ],
    });
  }

  crosshairHighlight({ timestamp }) {
    if (this.chart === undefined) return;

    const options = this.chart.getOption();
    if (!this.highlighting && options?.xAxis?.[0]?.type === "time") {
      const xPixel = this.chart.convertToPixel({ xAxisIndex: 0 }, timestamp);
      setTimeout(() => {
        this.chart?.setOption({
          graphic: [
            {
              id: "crosshair",
              type: "line",
              z: 1000,
              invisible: false,
              shape: {
                x1: xPixel,
                y1: 10,
                x2: xPixel,
                y2: this.chart.getHeight() - 20,
              },
              style: {
                stroke: "gray",
                lineWidth: 1,
                opacity: 0.8,
              },
            },
          ],
        });
      }, 0);
    }
  }

  resize() {
    this.chart?.resize();
  }

  hideError() {
    this.chartWrapTarget.classList.remove("hidden");
    this.errorTarget.classList.remove("show");
  }

  showError({ message, showConfigMessage }) {
    this.chartWrapTarget.classList.add("hidden");
    this.errorTarget.classList.add("show");
    const errorMessageTemplate = this.compileElement(
      this.errorMessageTemplateTarget,
    );
    this.errorTarget.innerHTML = errorMessageTemplate({
      message,
      showConfigMessage,
    });
  }

  loadEmptyQuery() {
    URL.updateParam("query", DEFAULT_QUERY);
  }

  showEmptyQuery() {
    this.chartTarget.classList.add("hidden");
    const colorQuery = hljs.highlight(DEFAULT_QUERY, {
      language: "badgerql",
    }).value;
    this.errorTarget.classList.add("show");
    const emptyTemplate = this.compileElement(this.emptyQueryTemplateTarget);
    this.errorTarget.innerHTML = emptyTemplate({ query: colorQuery });
  }

  setChartOptions(options) {
    this.chart?.setOption(options, true);
  }
}
